import { api } from "./api";

export interface Interview {
  id: string;
  expertFirstName: string;
  expertLastName: string;
  company: string;
  title: string;
  network: string;
  date?: Date | string | undefined;
  duration: string;
  initialInterviewCost: string;
  status:
    | "scheduled"
    | "completed"
    | "canceled"
    | "cancelled w/ fee"
    | "in progress"
    | "follow-up"
    | "follow-up scheduled"
    | "follow-up completed"
    | "secondary report";
  projectName?: string;
  angle?: string;
  anonymizedAttribution?: string;
  interviewDescription?: string;
  questionnaireCost?: string;
  expertRating?: number; // 1-5 star rating for expert performance
  transcriptStatus?: "processing" | "cleaning" | "ready" | "error" | null; // Transcript upload/cleaning status
}

export interface Project {
  id: string;
  name: string;
  description: string;
  status?: "active" | "inactive" | "archived";
  max_budget?: number;
  target_n?: number;
  role?: "owner" | "editor" | "viewer";
  client?: string;
  start_date: string; // Required ISO date string for project start date
}

// Calculate total cost for an interview
export const calculateTotalCost = (interview: Interview): number => {
  const initialCost = parseFloat(interview.initialInterviewCost || "0");
  const questionnaireCost = parseFloat(interview.questionnaireCost || "0");

  return initialCost + questionnaireCost;
};

export const getAllInterviews = async (): Promise<Interview[]> => {
  try {
    const response = await api.get("/api/interviews");
    // Convert string dates to Date objects only if they exist and are valid
    return response.data.map((interview: any) => ({
      ...interview,
      date: interview.date ? new Date(interview.date) : undefined,
    }));
  } catch (error) {
    console.error("Error fetching interviews:", error);
    return [];
  }
};

export const getInterviewsByProject = async (
  projectId: string
): Promise<Interview[]> => {
  try {
    const response = await api.get(`/api/interviews?projectId=${projectId}`);
    // Convert string dates to Date objects only if they exist and are valid
    return response.data.map((interview: any) => ({
      ...interview,
      date: interview.date ? new Date(interview.date) : undefined,
    }));
  } catch (error) {
    console.error("Error fetching interviews by project:", error);
    return [];
  }
};

export const getAllProjects = async (): Promise<Project[]> => {
  try {
    const response = await api.get("/api/projects");
    return response.data;
  } catch (error) {
    console.error("Error fetching projects:", error);
    return [];
  }
};

export const getOwnedProjects = async (): Promise<Project[]> => {
  try {
    const response = await api.get("/api/projects/owned");
    return response.data;
  } catch (error) {
    console.error("Error fetching owned projects:", error);
    return [];
  }
};

export const getSharedProjects = async (): Promise<Project[]> => {
  try {
    const response = await api.get("/api/projects/shared");
    return response.data;
  } catch (error) {
    console.error("Error fetching shared projects:", error);
    return [];
  }
};

export const getArchivedProjects = async (): Promise<Project[]> => {
  try {
    const response = await api.get("/api/projects/archived");
    return response.data;
  } catch (error) {
    console.error("Error fetching archived projects:", error);
    return [];
  }
};

export const toggleProjectStatus = async (
  projectId: string,
  newStatus?: "active" | "inactive" | "archived"
): Promise<boolean> => {
  try {
    const response = await api.post(
      `/api/projects/${projectId}/toggle-status`,
      { newStatus }
    );
    return response.data.success;
  } catch (error) {
    console.error("Error toggling project status:", error);
    return false;
  }
};

export const updateInterviews = async (
  interviews: Interview[]
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await api.post("/api/interviews/batch-update", interviews);
    return { success: response.data.success };
  } catch (error: any) {
    console.error("Error updating interviews in batch:", error);

    // Extract meaningful error message from API response
    let errorMessage = "Failed to save changes. Please try again.";

    if (error.response?.data?.error) {
      const apiError = error.response.data.error;

      // Handle database constraint violations generically
      if (
        typeof apiError === "string" &&
        apiError.includes("check constraint")
      ) {
        errorMessage =
          "Data validation failed. Please check your input values and try again.";
      } else if (
        typeof apiError === "string" &&
        apiError.includes("violates")
      ) {
        errorMessage =
          "Invalid data detected. Please verify your entries meet the required format.";
      } else if (typeof apiError === "object" && apiError.message) {
        errorMessage = `Database error: ${apiError.message}`;
      } else {
        errorMessage = `Server error: ${apiError}`;
      }
    } else if (error.response?.status === 403) {
      errorMessage = "You don't have permission to make this change.";
    } else if (error.response?.status === 404) {
      errorMessage = "Interview not found.";
    } else if (error.response?.status >= 500) {
      errorMessage = "Server error. Please try again later.";
    } else if (error.code === "NETWORK_ERROR" || !error.response) {
      errorMessage = "Network error. Check your connection and try again.";
    }

    return { success: false, error: errorMessage };
  }
};

// Create a new interview
export const createInterview = async (
  interview: Partial<Interview>,
  projectId: string
): Promise<Interview | null> => {
  try {
    const data = {
      ...interview,
      projectId: projectId,
      // Set default values if not provided
      status: interview.status || "scheduled",
      duration: interview.duration || "60",
      initialInterviewCost: interview.initialInterviewCost || "0",
    };

    const response = await api.post("/api/interviews", data);

    if (response.data.success) {
      // Convert string date to Date object if date exists
      const newInterview = {
        ...response.data.data,
        ...(response.data.data.date && {
          date: new Date(response.data.data.date),
        }),
      };
      return newInterview;
    }
    return null;
  } catch (error) {
    console.error("Error creating interview:", error);
    return null;
  }
};

// Delete an interview
export const deleteInterview = async (
  interviewId: string
): Promise<boolean> => {
  try {
    const response = await api.delete(`/api/interviews/${interviewId}`);
    return response.data.success;
  } catch (error) {
    console.error(`Error deleting interview ${interviewId}:`, error);
    return false;
  }
};

// Update a single interview
export const updateInterview = async (
  interview: Interview
): Promise<boolean> => {
  try {
    const response = await api.put(
      `/api/interviews/${interview.id}`,
      interview
    );
    return response.data.success;
  } catch (error) {
    console.error(`Error updating interview ${interview.id}:`, error);
    return false;
  }
};

// Calendar sync
export const syncCalendarEvents = async (): Promise<{
  success: boolean;
  message: string;
  addedCount: number;
  updatedCount?: number;
  cancelledCount?: number;
  completedCount?: number;
}> => {
  try {
    const response = await api.post("/api/calendar/sync", {});
    return response.data;
  } catch (error) {
    console.error("Error syncing calendar events:", error);
    throw error;
  }
};

export const createProject = async (
  project: Omit<Project, "id" | "role">
): Promise<Project | null> => {
  try {
    const response = await api.post("/api/projects", project);

    // Transform backend response to match Project interface
    if (response.data.success && response.data.project_id) {
      return {
        id: response.data.project_id, // Map project_id to id
        name: project.name,
        description: project.description || "",
        status: project.status || "active",
        max_budget: project.max_budget,
        target_n: project.target_n,
        role: "owner", // User who creates project is always owner
        client: project.client || "",
        start_date: project.start_date,
      };
    }

    return null;
  } catch (error) {
    console.error("Error creating project:", error);
    return null;
  }
};

export const updateProject = async (
  projectId: string,
  projectData: Partial<Omit<Project, "id" | "role">>
): Promise<Project | null> => {
  try {
    const response = await api.put(`/api/projects/${projectId}`, projectData);
    return response.data;
  } catch (error) {
    console.error("Error updating project:", error);
    return null;
  }
};

// User profile methods
export const getUserProfile = async (): Promise<{
  user_id: string;
  name: string;
  email: string;
  last_calendar_sync?: string;
  auth_method?: string;
}> => {
  try {
    const response = await api.get("/api/auth/user");
    return response.data;
  } catch (error) {
    console.error("Error fetching user profile:", error);
    throw error;
  }
};

export const updateUserSyncTime = async (lastSyncTime: string) => {
  try {
    const response = await api.put("/api/auth/user/sync-time", {
      last_calendar_sync: lastSyncTime,
    });
    return response.data;
  } catch (error) {
    console.error("Error updating user sync time:", error);
    throw error;
  }
};

// ------------------------------------------------------------------
// Project Sharing Types and Functions
// ------------------------------------------------------------------

export interface ProjectMember {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: "owner" | "editor" | "viewer";
  display_name?: string;
}

export interface User {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  display_name: string;
}

export const getProjectMembers = async (
  projectId: string
): Promise<ProjectMember[]> => {
  try {
    const response = await api.get(`/api/projects/${projectId}/members`);
    return response.data;
  } catch (error) {
    console.error("Error fetching project members:", error);
    return [];
  }
};

export const addProjectMember = async (
  projectId: string,
  email: string,
  role: "editor" | "viewer" = "editor"
): Promise<{ success: boolean; member?: ProjectMember; error?: string }> => {
  try {
    const response = await api.post(`/api/projects/${projectId}/members`, {
      email,
      role,
    });
    return {
      success: true,
      member: response.data.member,
    };
  } catch (error: any) {
    console.error("Error adding project member:", error);
    const errorMessage = error.response?.data?.error || "Failed to add member";
    return {
      success: false,
      error: errorMessage,
    };
  }
};

export const removeProjectMember = async (
  projectId: string,
  userId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    await api.delete(`/api/projects/${projectId}/members/${userId}`);
    return { success: true };
  } catch (error: any) {
    console.error("Error removing project member:", error);
    const errorMessage =
      error.response?.data?.error || "Failed to remove member";
    return {
      success: false,
      error: errorMessage,
    };
  }
};

export const searchUsers = async (email: string): Promise<User[]> => {
  try {
    const response = await api.get(
      `/api/users/search?query=${encodeURIComponent(email)}`
    );
    return response.data;
  } catch (error) {
    console.error("Error searching users:", error);
    return [];
  }
};
